import { useState } from "react";
import { Eye, EyeOff, Loader2, Sparkles, Box, ShieldCheck, Zap } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useDialog } from "../components/DialogProvider";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const dialog = useDialog();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const u = username.trim();
    if (!u || !password) {
      setError("Username dan password wajib diisi");
      return;
    }

    try {
      setLoading(true);

      await dialog.toastPromise(
        auth.login({ username: u, password }),
        {
          loading: "Memverifikasi akun...",
          success: () => "Login berhasil.",
          error: (e2) => e2?.response?.data?.message || e2.message || "Login gagal.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      navigate(from, { replace: true });
    } catch (e2) {
      setError(e2?.response?.data?.message || e2.message || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen bg-white">
      {/* Left Column: Dark Monochrome Hero */}
      <section className="relative hidden w-1/2 overflow-hidden bg-zinc-950 lg:flex lg:flex-col lg:justify-between p-12 border-r border-zinc-800">
        {/* Subtle glow */}
        <div className="absolute -left-[20%] top-[10%] h-[500px] w-[500px] rounded-full bg-zinc-800/20 blur-[120px]" />
        <div className="absolute -right-[20%] bottom-[10%] h-[400px] w-[400px] rounded-full bg-zinc-900/40 blur-[100px]" />
        <div className="absolute left-[20%] top-[40%] h-[300px] w-[300px] rounded-full bg-zinc-800/10 blur-[100px]" />
        
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_20%,transparent_100%)]" />

        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 shadow-sm">
            <Sparkles className="h-5 w-5 text-zinc-400" />
          </div>
          <span className="text-xl font-bold tracking-widest text-white">WEB BRRADS</span>
        </div>

        <div className="relative z-10 mb-8">
          <h2 className="text-[3.5rem] font-bold leading-[1.1] tracking-tight text-white mb-6">
            Elevate your <br />
            <span className="text-zinc-400">
              inventory system.
            </span>
          </h2>
          
          <div className="grid gap-4 sm:grid-cols-2 mt-12 max-w-lg">
            <div className="flex flex-col gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 p-5">
              <Box className="h-5 w-5 text-zinc-400" />
              <h3 className="text-sm font-medium text-white">Stock Opname</h3>
              <p className="text-xs text-zinc-500 leading-relaxed">Pantau barang masuk dan keluar dengan akurasi tinggi.</p>
            </div>
            <div className="flex flex-col gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 p-5">
              <Zap className="h-5 w-5 text-zinc-400" />
              <h3 className="text-sm font-medium text-white">POS Terintegrasi</h3>
              <p className="text-xs text-zinc-500 leading-relaxed">Transaksi kasir event yang terhubung langsung ke gudang.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Right Column: Clean Login Form */}
      <section className="flex w-full flex-col items-center justify-center bg-white px-4 py-12 lg:w-1/2 lg:px-16 xl:px-24">
        <div className="w-full max-w-[400px]">
          
          {/* Logo Section */}
          <div className="mb-10 flex flex-col items-center justify-center space-y-4">
            <img 
              src="/logo-text.png" 
              alt="BRRADS" 
              className="h-16 w-auto object-contain mix-blend-multiply sm:h-20" 
            />
          </div>

          <div className="text-center mb-10">
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900">
              Welcome Back
            </h1>
            <p className="mt-2 text-sm text-zinc-500">
              Masukkan kredensial Anda untuk melanjutkan.
            </p>
          </div>

          {error ? (
            <div className="mb-6 flex items-center gap-3 rounded-2xl border border-rose-200/60 bg-rose-50/50 px-4 py-3.5 text-sm font-semibold text-rose-700 shadow-sm">
              <div className="h-2 w-2 shrink-0 rounded-full bg-rose-500 animate-pulse" />
              {error}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 outline-none transition-colors placeholder:text-zinc-400 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                autoComplete="username"
                placeholder="Username"
              />
            </div>

            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 pr-10 text-sm text-zinc-900 outline-none transition-colors placeholder:text-zinc-400 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                autoComplete="current-password"
                placeholder="Password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
                aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
              </button>
            </div>

            <div className="flex justify-between items-center pt-2 pb-2">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input type="checkbox" className="h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 transition-colors group-hover:border-zinc-400" />
                <span className="text-sm text-zinc-600 group-hover:text-zinc-900 transition-colors">Remember me</span>
              </label>
              <button
                type="button"
                className="text-sm font-medium text-zinc-700 hover:text-zinc-900 transition-colors"
                onClick={() => dialog.toast({ type: 'info', title: 'Forgot Password', message: 'Silakan hubungi administrator sistem.' })}
              >
                Forgot Password?
              </button>
            </div>

            <button
              disabled={loading}
              className="group flex h-10 w-full items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {loading ? "Memproses..." : "Sign In"}
            </button>
          </form>
          
          <div className="mt-12 text-center flex items-center justify-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            <p className="text-[13px] font-medium text-slate-500">
              Secured Internal Access &bull; <span className="font-bold text-slate-900">BRRADS</span>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Login;
